import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Rows3, Calculator, PlusIcon } from "lucide-react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Selection, TabularColumnRow } from "./types";
import { useEditorConfig } from "./editor-config-context";
import { columnTextOptions } from "@brewbase/lib";

interface TabularSchemaPreviewProps {
  templateAttributeId: number;
  selection: Selection;
  isLocked: boolean;
  onSelectColumn: (attributeId: number, columnSortOrder: number) => void;
}

const newColumn = (
  overrides: Partial<TabularColumnRow> & { sort_order: number },
): Omit<TabularColumnRow, "id"> => ({
  lineage_id: null,
  name: "New Column",
  code: null,
  data_type: "number",
  is_index: false,
  is_computed: false,
  formula: null,
  formula_result_unit: null,
  formula_result_display_unit: null,
  required: false,
  unit: null,
  display_width: null,
  ...overrides,
});

const chipClass = (
  indented: boolean,
  isSelected: boolean,
  grabbable: boolean,
) =>
  "flex flex-col px-3 py-1.5 text-left border-r border-border min-w-[100px] hover:bg-muted/50 transition-colors " +
  (grabbable ? "cursor-grab " : "") +
  (indented ? "ml-6 " : "") +
  (isSelected
    ? "bg-olive-2 border-b-2! border-b-[var(--farmless-green)]"
    : "");

export function TabularSchemaPreview({
  templateAttributeId,
  selection,
  isLocked,
  onSelectColumn,
}: TabularSchemaPreviewProps) {
  const { useTabularColumnsApi } = useEditorConfig();
  const { columns, isLoading, replaceColumns, setColumnsCache } =
    useTabularColumnsApi(templateAttributeId);

  const sortedColumns = useMemo(
    () => [...(columns ?? [])].sort((a, b) => a.sort_order - b.sort_order),
    [columns],
  );

  // is_index is authoritative. The index column (if any) is pinned first and
  // not reorderable; every other column is freely reorderable.
  const indexColumn = useMemo(
    () => sortedColumns.find((c) => c.is_index) ?? null,
    [sortedColumns],
  );
  const reorderableColumns = useMemo(
    () => sortedColumns.filter((c) => !c.is_index),
    [sortedColumns],
  );

  /**
   * Save a column set that adds one column, and select that column so the
   * inspector opens on it. The cache is written first (the new column with a
   * temporary id until the server returns its own), so the inspector finds the
   * column at once and its edits build on the set that includes it.
   */
  const addAndSelect = (next: Omit<TabularColumnRow, "id">[], selectSortOrder: number) => {
    setColumnsCache(
      next.map((c) =>
        "id" in c && typeof c.id === "number" ? (c as TabularColumnRow) : { ...c, id: -Date.now() },
      ),
    );
    replaceColumns(next);
    onSelectColumn(templateAttributeId, selectSortOrder);
  };

  const handleAddIndexColumn = () => {
    if (isLocked) return;
    const bumped = sortedColumns.map((c) => ({
      ...c,
      sort_order: c.sort_order + 1,
    }));
    addAndSelect(
      [
        newColumn({
          name: "Time",
          data_type: "datetime",
          is_index: true,
          required: true,
          sort_order: 0,
        }),
        ...bumped,
      ],
      0,
    );
  };

  const handleAddColumn = () => {
    if (isLocked) return;
    const maxOrder =
      sortedColumns.length > 0
        ? Math.max(...sortedColumns.map((c) => c.sort_order))
        : -1;

    addAndSelect(
      [...sortedColumns, newColumn({ sort_order: maxOrder + 1 })],
      maxOrder + 1,
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const [activeId, setActiveId] = useState<number | null>(null);
  const activeColumn = useMemo(
    () => reorderableColumns.find((c) => c.id === activeId) ?? null,
    [reorderableColumns, activeId],
  );

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    if (isLocked) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = reorderableColumns.findIndex(
      (c) => String(c.id) === active.id,
    );
    const newIndex = reorderableColumns.findIndex(
      (c) => String(c.id) === over.id,
    );
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(reorderableColumns, oldIndex, newIndex);
    const ordered = indexColumn ? [indexColumn, ...reordered] : reordered;
    // Renumber sort_order only; is_index is preserved (authoritative).
    const renumbered = ordered.map((c, i) => ({ ...c, sort_order: i }));

    setColumnsCache(renumbered);
    replaceColumns(renumbered);
  };

  const selectedColumnSortOrder =
    selection?.type === "tabular-column" &&
    selection.attributeId === templateAttributeId
      ? selection.columnSortOrder
      : null;

  const containerRef = useRef<HTMLDivElement>(null);
  const [oddRowIds, setOddRowIds] = useState<Set<number>>(new Set());

  const computeRows = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const children = Array.from(el.children) as HTMLElement[];
    if (children.length === 0) return;

    const newOddIds = new Set<number>();
    let currentTop = children[0].offsetTop;
    let rowIndex = 0;
    let isFirstInRow = true;

    for (const child of children) {
      if (child.offsetTop > currentTop) {
        rowIndex++;
        currentTop = child.offsetTop;
        isFirstInRow = true;
      }
      if (rowIndex % 2 === 1 && isFirstInRow) {
        const id = Number(child.dataset.colIdx);
        if (!isNaN(id)) newOddIds.add(id);
      }
      isFirstInRow = false;
    }
    setOddRowIds(newOddIds);
  }, []);

  useLayoutEffect(() => {
    computeRows();
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(computeRows);
    observer.observe(el);
    return () => observer.disconnect();
  }, [computeRows, sortedColumns.length]);

  if (isLoading) {
    return (
      <div className="px-3 py-2 text-xs text-muted-foreground">
        Loading columns...
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e: DragStartEvent) => setActiveId(Number(e.active.id))}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <div ref={containerRef} className="flex flex-wrap border-t border-border">
        {indexColumn ? (
          <div
            data-col-idx={0}
            role="button"
            tabIndex={0}
            className={chipClass(
              oddRowIds.has(0),
              selectedColumnSortOrder === indexColumn.sort_order,
              false,
            )}
            onClick={(e) => {
              e.stopPropagation();
              onSelectColumn(templateAttributeId, indexColumn.sort_order);
            }}
          >
            <ColumnChipInner col={indexColumn} isIndex />
          </div>
        ) : (
          !isLocked && (
            <button
              data-col-idx={0}
              className={
                "flex items-center gap-1 px-3 py-1.5 text-xs text-warning-foreground bg-warning/20 hover:bg-warning/30 transition-colors min-w-[100px] border-r border-border" +
                (oddRowIds.has(0) ? " ml-6" : "")
              }
              title="This table has no row axis. Add one to index and order its rows."
              onClick={(e) => {
                e.stopPropagation();
                handleAddIndexColumn();
              }}
            >
              <Rows3 className="h-3 w-3" />
              Add row axis
            </button>
          )
        )}
        <SortableContext
          items={reorderableColumns.map((c) => String(c.id))}
          strategy={rectSortingStrategy}
        >
          {reorderableColumns.map((col, i) => {
            const idx = i + 1;
            return (
              <SortableColumnChip
                key={col.id}
                col={col}
                idx={idx}
                indented={oddRowIds.has(idx)}
                isSelected={selectedColumnSortOrder === col.sort_order}
                disabled={isLocked}
                onSelect={() =>
                  onSelectColumn(templateAttributeId, col.sort_order)
                }
              />
            );
          })}
        </SortableContext>
        {!isLocked && (
          <button
            data-col-idx={reorderableColumns.length + 1}
            className={
              "flex items-center gap-1 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors min-w-[80px]" +
              (oddRowIds.has(reorderableColumns.length + 1) ? " ml-6" : "")
            }
            onClick={(e) => {
              e.stopPropagation();
              handleAddColumn();
            }}
          >
            <PlusIcon className="h-3 w-3" />
            Add
          </button>
        )}
      </div>
      <DragOverlay dropAnimation={null}>
        {activeColumn && (
          <div className="flex flex-col px-3 py-1.5 text-left bg-background border border-border shadow-md min-w-[100px]">
            <ColumnChipInner col={activeColumn} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

function SortableColumnChip({
  col,
  idx,
  indented,
  isSelected,
  disabled,
  onSelect,
}: {
  col: TabularColumnRow;
  idx: number;
  indented: boolean;
  isSelected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(col.id), disabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-col-idx={idx}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={0}
      className={chipClass(indented, isSelected, !disabled)}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      <ColumnChipInner col={col} />
    </div>
  );
}

function ColumnChipInner({
  col,
  isIndex = false,
}: {
  col: TabularColumnRow;
  isIndex?: boolean;
}) {
  return (
    <>
      <span className="text-sm font-medium truncate flex items-center gap-1">
        {isIndex && (
          <Rows3 className="h-3 w-3 text-muted-foreground shrink-0" />
        )}
        {col.is_computed && (
          <Calculator className="h-3 w-3 text-muted-foreground shrink-0" />
        )}
        {col.name}
      </span>
      <span className="text-xs text-muted-foreground">
        {columnTextOptions(col) ? "dropdown" : col.data_type}
        {col.unit ? ` · ${col.unit}` : ""}
      </span>
    </>
  );
}
